import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';

// Runtime detection - works in both localhost and production
const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

function ScreenerPage({ user, setUser }) {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [filters, setFilters] = useState({
    category: 'ALL',
    minPrice: '',
    maxPrice: '',
    minChange: '',
    maxChange: '',
    searchTerm: ''
  });

  useEffect(() => {
    fetchAssets();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [assets, filters]);

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API}/assets`);
      setAssets(response.data);
    } catch (err) {
      console.error('Assets fetch error:', err);
    }
  };

  const applyFilters = () => {
    let filtered = [...assets];

    // Category filter
    if (filters.category !== 'ALL') {
      filtered = filtered.filter(a => a.category === filters.category);
    }

    // Price filters
    if (filters.minPrice) {
      filtered = filtered.filter(a => a.current_price >= parseFloat(filters.minPrice));
    }
    if (filters.maxPrice) {
      filtered = filtered.filter(a => a.current_price <= parseFloat(filters.maxPrice));
    }

    // Change filters
    if (filters.minChange) {
      filtered = filtered.filter(a => a.change_percent >= parseFloat(filters.minChange));
    }
    if (filters.maxChange) {
      filtered = filtered.filter(a => a.change_percent <= parseFloat(filters.maxChange));
    }

    // Search filter
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(a => 
        a.symbol.toLowerCase().includes(term) || 
        a.name.toLowerCase().includes(term)
      );
    }

    setFilteredAssets(filtered);
  };

  const resetFilters = () => {
    setFilters({
      category: 'ALL',
      minPrice: '',
      maxPrice: '',
      minChange: '',
      maxChange: '',
      searchTerm: ''
    });
  };

  const categories = [
    { id: 'ALL', name: 'Tümü' },
    { id: 'BORSA_ISTANBUL', name: 'BORSA İSTANBUL' },
    { id: 'ENDEKSLER', name: 'ENDEKSLER' },
    { id: 'EMTIA', name: 'EMTİA' },
    { id: 'DOVIZ', name: 'DÖVİZ' },
    { id: 'KRIPTO', name: 'KRİPTO' },
    { id: 'HISSELER', name: 'HİSSELER' }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar user={user} setUser={setUser} />
      
      <div className="max-w-7xl mx-auto p-4">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">🔍 Varlık Tarayıcı</h1>
          <p className="text-gray-400 text-sm sm:text-base">Kriterlerinize uygun varlıkları bulun</p>
        </div>

        {/* Filters */}
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <h3 className="font-bold text-lg">Filtreler</h3>
            <button
              onClick={resetFilters}
              className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded text-sm font-semibold transition-colors"
            >
              Sıfırla
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Search */}
            <div className="lg:col-span-3">
              <label className="block text-sm font-medium mb-2">Ara</label>
              <input
                type="text"
                placeholder="Sembol veya isim..."
                value={filters.searchTerm}
                onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium mb-2">Kategori</label>
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Min Price */}
            <div>
              <label className="block text-sm font-medium mb-2">Min. Fiyat (₺)</label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={filters.minPrice}
                onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
              />
            </div>

            {/* Max Price */}
            <div>
              <label className="block text-sm font-medium mb-2">Max. Fiyat (₺)</label>
              <input
                type="number"
                step="0.01"
                placeholder="1000.00"
                value={filters.maxPrice}
                onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
              />
            </div>

            {/* Min Change */}
            <div>
              <label className="block text-sm font-medium mb-2">Min. Değişim (%)</label>
              <input
                type="number"
                step="0.1"
                placeholder="-10"
                value={filters.minChange}
                onChange={(e) => setFilters({ ...filters, minChange: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
              />
            </div>

            {/* Max Change */}
            <div>
              <label className="block text-sm font-medium mb-2">Max. Değişim (%)</label>
              <input
                type="number"
                step="0.1"
                placeholder="10"
                value={filters.maxChange}
                onChange={(e) => setFilters({ ...filters, maxChange: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
              />
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-700 flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-bold">Sonuçlar</h2>
            <span className="text-sm text-gray-400">{filteredAssets.length} varlık bulundu</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-750">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Sembol</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Ad</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Fiyat</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Değişim</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase hidden lg:table-cell">Kategori</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredAssets.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-400">
                      Kriterlere uygun varlık bulunamadı
                    </td>
                  </tr>
                ) : (
                  filteredAssets.map(asset => (
                    <tr key={asset.id} className="hover:bg-gray-750 cursor-pointer" onClick={() => navigate('/')}>
                      <td className="px-3 sm:px-6 py-4 text-sm font-bold">{asset.symbol}</td>
                      <td className="px-3 sm:px-6 py-4 text-sm text-gray-400">{asset.name}</td>
                      <td className="px-3 sm:px-6 py-4 text-sm font-mono font-bold">₺{asset.current_price.toFixed(2)}</td>
                      <td className="px-3 sm:px-6 py-4 text-sm">
                        <span className={`font-semibold ${
                          asset.change_percent >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {asset.change_percent >= 0 ? '+' : ''}{asset.change_percent.toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 text-sm text-gray-400 hidden lg:table-cell">{asset.category}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScreenerPage;
